import React from 'react';

/**
 * VERUM Q — brand identity
 *
 * Mark system (per MIRROR.md doctrine):
 *
 *   LogoMark      — the open-Q glyph alone. Used as favicon, mobile nav,
 *                   any tight space where the wordmark would not fit.
 *                   Reads as "the unanswered question": a 95% closed circle
 *                   with a notch where the user's reply belongs.
 *
 *   LogoLockup    — open-Q + pipe + "VERUM | Q" wordmark. Used in nav,
 *                   onboarding header, footer, landing nav. The default
 *                   horizontal mark for the product UI.
 *
 *   LogoFull      — large pipe lockup for marketing surfaces (hero,
 *                   splash, social cards, app store).
 *
 *   LogoAnimated  — kept for the loader call sites. The mark itself fades
 *                   in once over 200ms; nothing else moves (doctrine
 *                   forbids motion beyond the single fade).
 *
 * Constants enforced:
 *   - currentColor only (so the mark inherits the surrounding ink colour
 *     and never carries an out-of-system fill).
 *   - JetBrains Mono / IBM Plex Mono / monospace stack.
 *   - Letter-spacing 0.04em for headline scale, 0.18em for small caps
 *     wordmarks (matches Mirror's typographic system in index.css).
 *   - 0.5–1.5px hairline strokes scaled to size, never thicker than the
 *     wordmark's stem weight.
 *   - Square caps. Square corners. No rounding above 2px anywhere.
 */

const FONT_STACK = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * The open-Q glyph.
 *
 * Construction (matches frontend/public/favicon.svg, scaled to 24×24):
 *   - Full circle, centre (12, 12), radius 9.
 *   - stroke-dasharray creates a 30° gap (4.712 units of the
 *     56.549-unit circumference) at the bottom-right.
 *   - The circle is rotated 30° around its centre so the gap lands
 *     between 30° and 60° (4–5 o'clock visually).
 *   - The tail is a short diagonal positioned slightly south of the
 *     gap centre — the ring with a hole and a separate short stroke,
 *     not one continuous gesture.
 *
 * Note on the tail offset (intentional, do not "fix"):
 *   The tail does not pass cleanly through the opening — it sits a
 *   few degrees south of the gap. This was a happy accident from the
 *   first working geometry, and we kept it: the visual friction
 *   reads as two independent gestures rather than one continuous
 *   stroke. Doctrine-consistent with "deliberate friction" — the
 *   glyph rewards a second look. If you rotate the tail to align
 *   with the gap, the whole mark loses its character.
 *
 * Why the dasharray approach (not an SVG arc):
 *   When two arc endpoints sit close to each other and you ask SVG
 *   to sweep the long way around, the renderer can place the circle
 *   centre in either of two valid positions — and browsers chose the
 *   wrong one, shoving the glyph into the bottom-right corner. A
 *   <circle> with explicit cx/cy/r is deterministic.
 *
 * Stroke width is parameterised so callers can scale the glyph
 * without the ring becoming a smear at small sizes or a ribbon at
 * large ones. Round caps + round joins keep the dash terminus from
 * shearing under pixel-snapping.
 */
function OpenQ({ size = 24, stroke = null }) {
  // Auto-scale stroke if not provided. Tuned by eye:
  //   16px → 1.5
  //   24px → 1.75
  //   32px → 2
  //   56px → 2.6
  //   80px → 3
  const computedStroke = stroke ?? Math.max(1.25, Math.min(3, size / 24 * 1.75));

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      {/*
        Circle with a wedge opened by stroke-dasharray.
          Circumference = 2·π·9 = 56.549
          Gap of 30° = 30/360 × 56.549 = 4.712
          Visible dash = 56.549 − 4.712 = 51.836
        transform="rotate(30 12 12)" puts the gap between 30° and 60°
        from the 3-o'clock origin (i.e. 4–5 o'clock visually).
      */}
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth={computedStroke}
        strokeLinecap="round"
        strokeDasharray="51.836 4.712"
        transform="rotate(30 12 12)"
      />
      {/*
        Tail: short diagonal positioned slightly south of the gap centre.
        Intentionally offset — see the component header comment for why
        this misalignment is doctrine-consistent and should not be
        "corrected" to align with the opening.
      */}
      <line
        x1="15"
        y1="15"
        x2="21"
        y2="21"
        stroke="currentColor"
        strokeWidth={computedStroke}
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * LogoMark — open-Q only.
 * Default size matches the existing call sites (22–32px nav contexts).
 * The wrapper preserves currentColor so it picks up text colour.
 */
export function LogoMark({ size = 24, className = '' }) {
  return (
    <span
      className={`inline-flex items-center justify-center text-foreground ${className}`}
      style={{ width: size, height: size }}
    >
      <OpenQ size={size} />
    </span>
  );
}

/**
 * LogoLockup — open-Q + pipe + wordmark.
 * The default horizontal mark for the product UI.
 *
 * Geometry:
 *   [ open-Q glyph ][ gap ][ "VERUM" ][ pipe ][ "Q" ]
 *
 * The Q-glyph at the start is the same character as the trailing Q in
 * the wordmark — they rhyme. The pipe between VERUM and Q is the
 * signature of the brand: a literal separator between sandhed and
 * spørgsmål.
 *
 * `size` here means the glyph height; the wordmark scales with it.
 */
export function LogoLockup({ size = 24, className = '' }) {
  // Wordmark sizing relative to glyph. Wordmark cap-height ≈ 0.7 × size.
  const wordSize = Math.round(size * 0.92);
  const pipeHeight = Math.round(size * 0.85);
  const pipeWidth = Math.max(0.75, size / 32);
  const gap = Math.max(6, Math.round(size * 0.4));
  const innerGap = Math.max(4, Math.round(size * 0.25));

  return (
    <span
      className={`inline-flex items-center text-foreground ${className}`}
      style={{ gap: gap }}
      role="img"
      aria-label="Verum Q"
    >
      <OpenQ size={size} />
      <span
        style={{
          fontFamily: FONT_STACK,
          fontSize: wordSize,
          fontWeight: 500,
          letterSpacing: '0.04em',
          lineHeight: 1,
          color: 'currentColor',
          display: 'inline-flex',
          alignItems: 'center',
          gap: innerGap,
        }}
      >
        <span>VERUM</span>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: pipeWidth,
            height: pipeHeight,
            background: 'currentColor',
          }}
        />
        <span>Q</span>
      </span>
    </span>
  );
}

/**
 * LogoFull — large pipe lockup for marketing surfaces.
 * Same geometry as LogoLockup but with proportions tuned for hero scale
 * (typically 48–96px high). The glyph leads, the wordmark follows.
 */
export function LogoFull({ height = 48, className = '' }) {
  return <LogoLockup size={height} className={className} />;
}

/**
 * LogoAnimated — loader version.
 * The mark itself fades in once. No pulse, no draw, no float (doctrine
 * forbids continuous motion).
 */
export function LogoAnimated({ size = 56, className = '' }) {
  return (
    <span
      className={`inline-flex items-center justify-center text-foreground animate-fade-in ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <OpenQ size={size} />
    </span>
  );
}
